import React from 'react';
import { useTheme } from './ThemeContext';

const getFeedbackMessage = (interval) => {
  const deviation = interval - 1000;
  if (Math.abs(deviation) <= 50) return 'PERFECT! 🎯';
  if (Math.abs(deviation) <= 200) return deviation > 0 ? 'Good, but late 👍' : 'Good, but early 👍';
  return deviation > 0 ? 'Too late! 🕐' : 'Too soon! 🏃';
};

const getFeedbackColor = (interval) => {
  const deviation = Math.abs(interval - 1000);
  if (deviation <= 50) return '#10B981';
  if (deviation <= 200) return '#F59E0B';
  return '#EF4444';
};

const LifeBar = ({ remainingLife = 1000, clickCount, timeArray }) => {
  const { theme, isAscii: isAnyAscii } = useTheme();
  const lifePercent = Math.max(0, (remainingLife / 1000) * 100);
  const barColor = lifePercent > 50 ? '#10B981' : lifePercent > 25 ? '#F59E0B' : '#EF4444';

  // clickCount starts at 1 on the start click; game clicks = clickCount - 1
  const gameClicks = Math.max(0, clickCount - 1);

  // Compute feedback directly in render — no useState/useEffect needed
  let feedback = 'Click to start!';
  let feedbackColor = '#4A5568';
  if (clickCount >= 2) {
    const interval = timeArray[timeArray.length - 1] - timeArray[timeArray.length - 2];
    feedback = getFeedbackMessage(interval);
    feedbackColor = getFeedbackColor(interval);
  }

  if (isAnyAscii) {
    const color = theme === 'dark' ? '#33ff33' : '#1a1a1a';
    const totalChars = 28;
    const filledChars = Math.round((lifePercent / 100) * totalChars);
    const emptyChars = totalChars - filledChars;
    const bar = '█'.repeat(filledChars) + '░'.repeat(emptyChars);
    return (
      <div style={{ fontFamily: '"Courier New", Courier, monospace', color, padding: '0.5rem 0', width: '100%' }}>
        <div style={{ color: feedbackColor, textAlign: 'center', marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 'bold' }}>
          {feedback}
        </div>
        <div style={{ marginBottom: '0.4rem' }}>
          Life: [{bar}]
        </div>
        <div>Clicks: {gameClicks}</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '1rem 0' }}>
      {/* Feedback — absolutely positioned so it never affects the bar's width */}
      <div style={{ position: 'relative', height: '1.75rem', marginBottom: '1.5rem' }}>
        <div style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          fontSize: '1.25rem',
          fontWeight: 'bold',
          color: feedbackColor,
        }}>
          {feedback}
        </div>
      </div>

      {/* Life bar */}
      <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#4A5568' }}>
        <span>Life</span>
        <span>{remainingLife.toFixed(0)} ms</span>
      </div>
      <div style={{
        width: '100%',
        height: '24px',
        backgroundColor: '#E2E8F0',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${lifePercent}%`,
          backgroundColor: barColor,
          borderRadius: '12px',
          transition: 'width 0.2s ease, background-color 0.2s ease',
        }} />
      </div>

      {/* Click counter */}
      <div style={{
        textAlign: 'center',
        marginTop: '1rem',
        fontSize: '1.5rem',
        fontWeight: '600',
        color: '#2D3748',
      }}>
        {gameClicks} {gameClicks === 1 ? 'click' : 'clicks'}
      </div>
    </div>
  );
};

export default LifeBar;
